// Tout le vocabulaire de l'IA, à un seul endroit.
//
// Trois axes qui ne disent pas la même chose, et qu'il ne faut surtout pas mélanger :
//
//   la chaîne   — **où** dans le cycle de vie la personne intervient
//   les terrains — **sur quoi** elle l'a fait
//   les postures — **quel rôle** elle joue vis-à-vis des autres
//
// Le niveau global (1 à 5) reste un jugement d'ensemble, distinct de ces axes.
//
// Ces listes sont fermées, et c'est le but. Avec des libellés libres, « industrialisation »,
// « mise en prod » et « MEP » deviennent trois choses, et le rapprochement ne tient plus :
// c'est parce que la mission et le profil se rangent dans le même vocabulaire qu'on peut
// les comparer terme à terme plutôt que par ressemblance de texte.

/**
 * La chaîne de valeur d'un projet IA. Les gens sont rarement bons de bout en bout, et les
 * missions échouent presque toujours au même endroit : le passage du POC à la production.
 * C'est l'axe qui porte la promesse du cabinet — accompagner jusqu'au succès.
 */
export const ETAPES_CHAINE = [
  { cle: 'cadrage', libelle: 'Cadrage', aide: "identifier les usages qui valent la peine, chiffrer, arbitrer" },
  { cle: 'poc', libelle: 'Preuve de concept', aide: 'construire vite ce qui démontre, ou qui écarte' },
  { cle: 'industrialisation', libelle: 'Industrialisation', aide: 'passer du prototype au système qui tient' },
  { cle: 'run', libelle: 'Exploitation', aide: 'faire vivre, surveiller la dérive, reprendre la main' },
  { cle: 'gouvernance', libelle: 'Gouvernance', aide: 'cadrer les usages, les risques, la conformité' },
] as const;
export type EtapeChaine = (typeof ETAPES_CHAINE)[number]['cle'];
export const CLES_CHAINE = ETAPES_CHAINE.map((e) => e.cle) as EtapeChaine[];

/**
 * Sur quels systèmes. « IA » ne veut rien dire tout seul : quelqu'un qui a construit des
 * chaînes documentaires n'est pas quelqu'un qui a fait de la prévision de demande, et un
 * CV qui dit « IA » ne permet d'écarter ni de retenir personne.
 */
export const TERRAINS_IA = [
  { cle: 'generatif', libelle: 'Génératif · LLM', aide: 'rédaction, extraction, assistance, agents conversationnels' },
  { cle: 'agents', libelle: 'Agents · automatisation', aide: 'enchaînements outillés, actions autonomes, orchestration' },
  { cle: 'documentaire', libelle: 'Recherche documentaire · RAG', aide: 'indexation, recherche sémantique, réponse sourcée' },
  { cle: 'predictif', libelle: 'Analytique · prédictif', aide: 'prévision, scoring, optimisation, séries temporelles' },
  { cle: 'vision', libelle: 'Vision · signal', aide: 'image, son, détection, contrôle qualité' },
  { cle: 'plateforme', libelle: 'Plateforme · MLOps · data', aide: "ce qui rend le reste possible : données, déploiement, surveillance" },
] as const;
export type TerrainIA = (typeof TERRAINS_IA)[number]['cle'];
export const CLES_TERRAINS = TERRAINS_IA.map((t) => t.cle) as TerrainIA[];

/**
 * Les postures. **Ce n'est pas une échelle** : « Formateur » n'est pas un cran au-dessus de
 * « Builder », c'est un autre rôle, et la même personne peut tenir les deux. D'où plusieurs
 * postures par dossier plutôt qu'un cran unique — forcer un choix entre deux vérités
 * aurait fait perdre celle qu'on ne coche pas.
 */
export const POSTURES_IA = [
  'Utilisateur', 'Utilisateur avancé', 'Concepteur', 'Builder', 'Architecte',
  'Formateur', 'Acculturateur',
] as const;
export type PostureIA = (typeof POSTURES_IA)[number];

/** Ce qu'il faut avoir fait pour mériter chaque posture. Sert à la lecture comme à l'entretien. */
export const PREUVE_POSTURE: Record<PostureIA, string> = {
  'Utilisateur': "se sert d'outils génératifs dans son propre travail",
  'Utilisateur avancé': "a outillé son travail ou celui de son équipe, au-delà de l'usage courant",
  'Concepteur': "a conçu des usages, des consignes ou des parcours destinés à d'autres",
  'Builder': 'a construit et livré un système reposant sur des modèles',
  'Architecte': "a défini une architecture ou une gouvernance IA à l'échelle d'une organisation",
  'Formateur': "a formé des équipes, construit un parcours ou animé une montée en compétence",
  // Former suppose des gens qui viennent ; acculturer, c'est faire bouger ceux qui ne
  // demandaient rien. C'est le rôle le plus proche de ce que WAKEA vend, et le plus rare.
  'Acculturateur': "a embarqué une direction ou un comité qui ne demandait rien",
};

/**
 * Quatre crans par axe, et pas six. Un dossier ne porte pas six degrés de certitude ;
 * en afficher six inventerait une précision que ni le modèle ni le consultant n'ont.
 * Le zéro dit « non constaté », jamais « faible » : la nuance sépare une information
 * d'un jugement, et un dossier neuf est plein de zéros sans que personne n'y soit pour rien.
 */
export const NIVEAUX_AXE: Record<number, string> = {
  0: 'non constaté',
  1: 'constaté',
  2: 'solide · avancé',
  3: 'expert · fait référence',
};
export const NIVEAU_MAX = 3;

/** Le niveau d'ensemble, de 1 à 5. Il nuance les postures sans les remplacer. */
export const NIVEAUX_IA: Record<number, string> = {
  1: 'découvre',
  2: 'pratique',
  3: 'maîtrise',
  4: 'fait référence dans son périmètre',
  5: 'fait autorité au-delà de son organisation',
};

export type Axe = Partial<Record<string, number>>;

/** Lit un axe rangé en jsonb : une clé inconnue ou un cran hors échelle vaut zéro. */
export const niveauAxe = (axe: Axe | null | undefined, cle: string): number => {
  const v = axe?.[cle];
  return typeof v === 'number' && Number.isInteger(v) && v >= 0 && v <= NIVEAU_MAX ? v : 0;
};

/** Les clés d'un axe effectivement constatées, du plus fort au plus faible. */
export const constate = <T extends string>(axe: Axe | null | undefined, cles: T[]) =>
  cles.map((cle) => ({ cle, niveau: niveauAxe(axe, cle) }))
      .filter((x) => x.niveau > 0)
      .sort((a, b) => b.niveau - a.niveau);
